import { Link } from 'react-router-dom'
import { Facebook, Twitter, Instagram, Youtube, Mail, Shield, Lock, Eye } from 'lucide-react'
import { useState } from 'react'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'

export default function Footer() {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email.trim()) {
      toast.error('Por favor, ingresa tu email')
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      toast.error('Por favor, ingresa un email válido')
      return
    }

    setIsSubmitting(true)

    try {
      const { error } = await supabase
        .from('newsletter_subscribers')
        .insert({ 
          email: email.trim().toLowerCase(),
          status: 'active',
          is_verified: false
        })

      if (error) {
        // Si ya existe el email
        if (error.code === '23505') {
          toast.info('Este email ya está suscrito al newsletter')
        } else {
          console.error('Error al suscribir:', error)
          toast.error('Error al procesar la suscripción')
        }
      } else {
        toast.success('¡Suscripción exitosa! Revisa tu email para confirmar.')
        setEmail('')
      }
    } catch (error) {
      console.error('Error inesperado:', error)
      toast.error('Error al procesar la suscripción')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <footer className="bg-neutral-800 text-neutral-300 pt-12 pb-6">
      <div className="container mx-auto px-4">
        {/* Grid de 4 columnas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Columna 1: Acerca de */}
          <div>
            <h3 className="font-heading font-bold text-white mb-4">Acerca de</h3>
            <p className="text-sm mb-3">
              Educación que convierte en participación cívica
            </p>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/about" className="hover:text-white transition-colors">
                  ¿Quiénes somos?
                </Link>
              </li>
              <li>
                <Link to="/impact" className="hover:text-white transition-colors">
                  Impacto anual
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-white transition-colors">
                  Contacto
                </Link>
              </li>
            </ul>
          </div>

          {/* Columna 2: Productos */}
          <div>
            <h3 className="font-heading font-bold text-white mb-4">Productos</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/cursos" className="hover:text-white transition-colors">
                  Explorar Cursos
                </Link>
              </li>
              <li>
                <Link to="/campanas" className="hover:text-white transition-colors">
                  Descubrir Campañas
                </Link>
              </li>
              <li>
                <Link to="/comunidad" className="hover:text-white transition-colors">
                  Comunidad
                </Link>
              </li>
              <li>
                <Link to="/faq" className="hover:text-white transition-colors">
                  Preguntas Frecuentes
                </Link>
              </li>
            </ul>
          </div>

          {/* Columna 3: Legal */}
          <div>
            <h3 className="font-heading font-bold text-white mb-4">Legal</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/legal" className="hover:text-white transition-colors">
                  Aviso Legal
                </Link>
              </li>
              <li>
                <Link to="/privacidad" className="hover:text-white transition-colors">
                  Privacidad
                </Link>
              </li>
              <li>
                <Link to="/cookies" className="hover:text-white transition-colors">
                  Cookies
                </Link>
              </li>
              <li>
                <Link to="/transparencia" className="hover:text-white transition-colors">
                  Transparencia
                </Link>
              </li>
              <li>
                <Link to="/terminos" className="hover:text-white transition-colors">
                  Términos de Uso
                </Link>
              </li>
            </ul>
          </div>

          {/* Columna 4: Conecta */}
          <div>
            <h3 className="font-heading font-bold text-white mb-4">Conecta</h3>
            <p className="text-sm mb-3">
              Semanalmente: nuevos cursos y oportunidades
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex space-x-2 mb-4">
              <input
                type="email"
                placeholder="Tu email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
                className="flex-1 px-3 py-2 rounded bg-neutral-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-education disabled:opacity-50"
              />
              <button 
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-education text-white rounded hover:bg-education-dark transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? '...' : 'Ir'}
              </button>
            </form>
            <div className="flex space-x-3 mb-4">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-neutral-400 hover:text-white transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-neutral-400 hover:text-white transition-colors"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-neutral-400 hover:text-white transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-neutral-400 hover:text-white transition-colors"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
            {/* Certificaciones */}
            <div className="flex flex-wrap gap-2 text-xs">
              <div className="flex items-center space-x-1 bg-neutral-700 px-2 py-1 rounded">
                <Shield className="w-3 h-3" />
                <span>WCAG AAA</span>
              </div>
              <div className="flex items-center space-x-1 bg-neutral-700 px-2 py-1 rounded">
                <Lock className="w-3 h-3" />
                <span>SSL Secure</span>
              </div>
              <div className="flex items-center space-x-1 bg-neutral-700 px-2 py-1 rounded">
                <Eye className="w-3 h-3" />
                <span>RGPD</span>
              </div>
            </div>
          </div>
        </div>

        {/* Información adicional del footer */}
        <div className="border-t border-neutral-700 pt-6 text-center">
          <p className="text-sm text-neutral-400 mb-2">
            © 2025 Learn to Lead Change - Todos los derechos reservados
          </p>
          <p className="text-xs text-neutral-500">
            Contenidos educativos bajo licencia Creative Commons BY-NC-SA 4.0
          </p>
        </div>
      </div>
    </footer>
  )
}
